import React, { createElement, Fragment, ReactElement } from "react"
import { NODE_PARAGRAPH, render, RenderOptions } from "storyblok-rich-text-react-renderer"

import { getLanguageFromSbLocale } from "@/lib/locale/locale-common-utils"
import { AssetStoryblok, RichtextStoryblok } from "@/components/storyblok/component-types"

interface EmailTemplateProps {
  sbLocale: string
  image?: AssetStoryblok
  body: RichtextStoryblok
  footerLeft: RichtextStoryblok
  footerRight: RichtextStoryblok
  footerBottom: RichtextStoryblok
}

const renderNewLines = (textParts: string[], type: string, props: any) => {
  return (
    <>
      {textParts.map((item, index) => (
        <Fragment key={index}>
          {createElement(type, props, item)}
          {index < textParts.length - 1 && <br />}
        </Fragment>
      ))}
    </>
  )
}

const textSpanFontSizes: Record<string, string> = {
  xs: "9px",
  sm: "11px",
  base: "12px",
  lg: "14px",
  xl: "16px",
  "2xl": "20px",
  "3xl": "24px",
}

const textSpanLineHeights: Record<string, string> = {
  none: "1",
  tight: "1.25",
  smug: "1.375",
  snug: "1.375",
  normal: "1.5",
  relaxed: "1.625",
  loose: "2",
}

const validTextSpanElements = new Set(["span", "p", "h1", "h2", "h3", "h4", "h5", "h6"])

function getColorValue(color: unknown): string | undefined {
  if (typeof color === "string") {
    return color || undefined
  }

  if (!color || typeof color !== "object") {
    return undefined
  }

  const colorRecord = color as Record<string, unknown>
  const possibleValues = [
    colorRecord.colorHex,
    colorRecord.color,
    colorRecord.value,
    colorRecord.hex,
    colorRecord.rgba,
    colorRecord.selected,
    colorRecord.result,
  ]

  return possibleValues.find((value): value is string => typeof value === "string" && value.length > 0)
}

function renderEmailNestedBloks(content: unknown): ReactElement[] | null {
  if (!Array.isArray(content)) {
    return null
  }

  return content.map((nestedBlok, index) => {
    if (!nestedBlok || typeof nestedBlok !== "object") {
      return <Fragment key={index} />
    }
    const blok = nestedBlok as Record<string, unknown>
    return (
      <Fragment key={(blok._uid as string | undefined) ?? index}>
        {renderEmailBlok((blok.component as string | undefined) ?? "", blok)}
      </Fragment>
    )
  })
}

function renderEmailNestedBloksElement(content: unknown): ReactElement | null {
  const nestedBloks = renderEmailNestedBloks(content)
  return nestedBloks ? <>{nestedBloks}</> : null
}

function renderEmailBlok(name: string, props: Record<string, unknown>) {
  if (name === "ub_text_span") {
    const requestedElement = props.type === "none" ? undefined : props.type
    const element = typeof requestedElement === "string" && validTextSpanElements.has(requestedElement) ? requestedElement : "span"
    const color = getColorValue(props.ub_color_picker) ?? getColorValue(props.color)
    const alignment = props.alignment ?? props.align ?? props.text_align
    const style: React.CSSProperties = {
      color,
      fontSize: typeof props.size === "string" ? textSpanFontSizes[props.size] : undefined,
      fontWeight: props.bold ? "bold" : undefined,
      fontStyle: props.italic ? "italic" : undefined,
      lineHeight: typeof props.line_height === "string" ? textSpanLineHeights[props.line_height] : undefined,
      textAlign: alignment === "left" || alignment === "center" || alignment === "right" ? alignment : undefined,
      textDecoration: props.underline ? "underline" : undefined,
    }

    return createElement(
      element,
      { style },
      String(props.text_before ?? props.text ?? ""),
      renderEmailNestedBloksElement(props.content),
      String(props.text_after ?? "")
    )
  }

  if (name === "ub_text" || name === "text") {
    return <span>{props.content as string}</span>
  }

  if (props.content) {
    return renderEmailNestedBloksElement(props.content) ?? <span>{String(props.content)}</span>
  }

  return <span>{String(props.text_before ?? props.text ?? props.label ?? props.name ?? "")}</span>
}

const renderOptions: RenderOptions = {
  defaultBlokResolver: (name, props) => {
    props.component = name
    return renderEmailBlok(name, props)
  },
  nodeResolvers: {
    [NODE_PARAGRAPH]: (children: ReactElement<any>[]) => {
      // todo solve with recoursive function
      const textItemWithNewLine = children?.find((child) => child.toString().includes("\n"))
      if (textItemWithNewLine) {
        const textParts: string[] = textItemWithNewLine.toString().split("\n")
        return <p>{renderNewLines(textParts, "span", null)}</p>
      }

      const itemWithNewLine = children?.find((child: ReactElement<any>) =>
        child?.props?.children?.toString().includes("\n")
      )
      if (itemWithNewLine) {
        const textParts: string[] = itemWithNewLine.props.children.split("\n")
        return <p>{renderNewLines(textParts, itemWithNewLine.type.toString(), itemWithNewLine.props)}</p>
      }

      const itemWithNewLine2 = children?.find((child: ReactElement<any>) =>
        child?.props?.children?.props?.children?.toString().includes("\n")
      )
      if (itemWithNewLine2) {
        const textParts: string[] = itemWithNewLine2.props.children.props.children.split("\n")
        return (
          <p>
            {createElement(
              itemWithNewLine2.type,
              itemWithNewLine2.props,
              renderNewLines(
                textParts,
                itemWithNewLine2.props.children.type.toString(),
                itemWithNewLine2.props.children.props
              )
            )}
          </p>
        )
      }

      const itemWithNewLine3 = children?.find((child: ReactElement<any>) =>
        child?.props?.children?.props?.children?.props?.children?.toString().includes("\n")
      )
      if (itemWithNewLine3) {
        const textParts: string[] = itemWithNewLine3.props.children.props.children.props.children.split("\n")
        return (
          <p>
            {createElement(
              itemWithNewLine3.type,
              itemWithNewLine3.props,
              createElement(
                itemWithNewLine3.props.children.type,
                itemWithNewLine3.props.children.props,
                renderNewLines(
                  textParts,
                  itemWithNewLine3.props.children.props.children.type.toString(),
                  itemWithNewLine3.props.children.props.children.props
                )
              )
            )}
          </p>
        )
      }

      return <p>{children}</p>
    },
  },
}

const EmailTemplateContact = (props: EmailTemplateProps) => {
  const language = getLanguageFromSbLocale(props.sbLocale).toUpperCase()

  return (
    <html>
      <body
        lang={language}
        style={{
          background: "#D8D8D8",
          fontFamily: "Arial",
        }}
      >
        <table style={{ borderSpacing: 0, borderCollapse: "collapse", width: "450.0pt" }}>
          <tbody>
            {props.image && (
              <tr>
                <td style={{ background: "white", padding: "15.0pt 15.0pt 15.0pt 15.0pt" }}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={props.image.filename ?? ""} alt={props.image.alt ?? ""} />
                </td>
              </tr>
            )}
            <tr>
              <td style={{ background: "#F5F5F5", padding: "30.0pt 22.5pt 60.0pt 22.5pt", lineHeight: "16.5pt" }}>
                <div>{render(props.body, renderOptions)}</div>
              </td>
            </tr>
            <tr>
              <td
                style={{
                  background: "#7D7D7D",
                  padding: "30.0pt 22.5pt 60.0pt 22.5pt",
                }}
              >
                <table
                  style={{
                    borderSpacing: 0,
                    borderCollapse: "collapse",
                    width: "100%",
                    fontSize: "9.0pt",
                    lineHeight: "13.5pt",
                  }}
                >
                  <tbody>
                    <tr>
                      <td style={{ verticalAlign: "top" }}>{render(props.footerLeft)}</td>
                      <td style={{ verticalAlign: "top" }}>{render(props.footerRight)}</td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td
                style={{
                  background: "#333333",
                  padding: "11.25pt 22.5pt 11.25pt 22.5pt",
                  fontSize: "9.0pt",
                }}
              >
                {render(props.footerBottom)}
              </td>
            </tr>
          </tbody>
        </table>
      </body>
    </html>
  )
}

export default EmailTemplateContact
